import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectAllAccounts, selectCurrentAccount, selectGuestAccounts } from "fond/api";
import { AccountAccess } from "fond/types";

/**
 * Hook to determine the user's access level for the current account.
 *
 * @returns "member" if the user is a member of the account,
 *          "guest" if the user has access to shared items but is not a member,
 *          "none" if there is no current account or the user has no access
 */
export const useGetAccountAccess = (): AccountAccess => {
  const currentAccount = useSelector(selectCurrentAccount);
  const userAccounts = useSelector(selectAllAccounts);
  const guestAccounts = useSelector(selectGuestAccounts);

  return useMemo(() => {
    if (!currentAccount?.ID) {
      return "none";
    }

    const isMember = userAccounts.some((userAccount) => userAccount.ID === currentAccount.ID);
    if (isMember) return "member";

    const isGuest = guestAccounts.some((guestAccount) => guestAccount.ID === currentAccount.ID);
    if (isGuest) return "guest";

    return "none";
  }, [currentAccount?.ID, guestAccounts, userAccounts]);
};
